import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, Tooltip, Typography } from '@mui/material';
import { voiceApi, errorMessage } from './voiceApi';
import { useVoice } from './VoiceContext';

const STAFF_HELP = '부재중 전화 페이지의 담당 지정 · 담당자 연결 목록에 나옵니다. 아무도 켜지 않으면 모든 관리자가 나옵니다.';

/**
 * Per admin, two switches: 전화 — may place calls and see recordings (env-granted admins show as locked); 담당자 목록 —
 * listed in the Missed Calls 담당 지정 / 담당자 연결 menus. Managers (VOICE_ADMIN_EMAILS) only.
 */
const VoicePermissionsDialog = ({ open, onClose }) => {
    const voice = useVoice();
    const [admins, setAdmins] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);   // `${uuid}:${switch}` while that switch saves

    useEffect(() => {
        if (!open) return;
        voiceApi.admins().then((d) => { setAdmins(d.admins); setError(null); }).catch((err) => setError(errorMessage(err)));
    }, [open]);

    const save = async (admin, which, request, patch) => {
        setBusy(`${admin.uuid}:${which}`);
        try {
            const res = await request();
            setAdmins((prev) => prev.map((a) => (a.uuid === admin.uuid ? { ...a, ...patch(res) } : a)));
            setError(null);
            return res;
        } catch (err) {
            setError(errorMessage(err));
            return null;
        } finally {
            setBusy(null);
        }
    };

    const toggleCalls = async (admin) => {
        const res = await save(admin, 'calls', () => voiceApi.setPermission(admin.uuid, !admin.can_voice_call),
            (r) => ({ can_voice_call: r.can_voice_call, source: r.source }));
        if (res && admin.uuid === voice?.me?.admin?.uuid) voice.refreshMe?.(); // my own switch takes effect right away
    };
    const toggleStaff = (admin) => save(admin, 'staff', () => voiceApi.setMissedCallsStaff(admin.uuid, !admin.missed_calls_staff),
        (r) => ({ missed_calls_staff: r.missed_calls_staff }));

    const nobodyListed = admins.length > 0 && !admins.some((a) => a.missed_calls_staff);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Call permission</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Admins with this permission can place calls and open the recordings and transcripts of the calls they placed. Only the accounts listed in the server's VOICE_ADMIN_EMAILS can change this permission or open other admins' calls. A change applies for other admins the next time they load the dashboard.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    담당자 목록: {STAFF_HELP}
                </Typography>
                {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ width: 58, textAlign: 'center' }}>전화</Typography>
                    <Tooltip title={STAFF_HELP}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 58, textAlign: 'center' }}>담당자 목록</Typography>
                    </Tooltip>
                </Box>
                {nobodyListed && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        아직 아무도 담당자 목록에 없어 모든 관리자가 나옵니다.
                    </Typography>
                )}
                {admins.map((a) => {
                    const name = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email;
                    return (
                        <Box key={a.uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap>{a.first_name} {a.last_name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap display="block">{a.email}</Typography>
                            </Box>
                            <Tooltip title={a.source === 'env' ? 'Granted by the server configuration (VOICE_ADMIN_EMAILS); cannot be changed here' : ''}>
                                <Box component="span" sx={{ width: 58, textAlign: 'center' }}>
                                    <Switch
                                        checked={Boolean(a.can_voice_call)}
                                        disabled={busy === `${a.uuid}:calls` || a.source === 'env'}
                                        onChange={() => toggleCalls(a)}
                                        inputProps={{ 'aria-label': `${name} 전화` }}
                                    />
                                </Box>
                            </Tooltip>
                            <Box component="span" sx={{ width: 58, textAlign: 'center' }}>
                                <Switch
                                    checked={Boolean(a.missed_calls_staff)}
                                    disabled={busy === `${a.uuid}:staff`}
                                    onChange={() => toggleStaff(a)}
                                    inputProps={{ 'aria-label': `${name} 담당자 목록` }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

export default VoicePermissionsDialog;
